import { useComponentTypesContext } from "@/pages/configurations/componentTypes/context/ComponentTypeContext";
import { getComponentTypeByPart } from "@/pages/configurations/partComponentTypes/repositories/PartComponentTypeRepository";
import {
	Button,
	CircularProgress,
	Grid,
	Snackbar,
	Theme,
	createStyles,
	makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { ErrorModal } from "@/components/shared/modal/error-modal";
import Modal from "@/components/shared/modal/modal";
import { PartComponentType } from "@/pages/configurations/partComponentTypes/models/PartComponentType";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { Part } from "../../models/Part";
import {
	getPartById,
	updateComponentTypeByPart,
} from "../../repositories/PartsRepository";
import { DataTable } from "./DataTable/DataTableCT";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
	}),
);

type Props = {
	partId: number | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const ComponentTypesList = ({ partId, show, onHide }: Props) => {
	const classes = useStyles();
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [componentTypesSelected, setComponentTypesSelected] = useState([]);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState("");

	const [part, setPart] = useState<Part | null>(null);

	const [partComponentTypeForEdit, setPartComponentTypeForEdit] =
		useState<PartComponentType | null>(null);
	const [partComponentTypes, setPartComponentTypes] = useState<{
		[ctypeId: number]: PartComponentType;
	}>({});

	const [error, setError] = useState<string | null>(null);
	const [showErrorModal, setShowErrorModal] = useState(false);

	const {
		state: { componentTypes },
	} = useComponentTypesContext();

	const {
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<PartComponentType>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};

	const onSubmit: SubmitHandler<PartComponentType> = async (data) => {
		setIsSubmitLoading(true);
		try {
			const res = await updateComponentTypeByPart(
				partId,
				componentTypesSelected,
			);

			if (res?.ok) {
				setMessage("The part was updated successfully");
				setMessageType("success");
				onHide(true);
			}
		} catch (e) {
			setMessage(`Error, please try again`);
			setMessageType("error");

			setError(e as string);
			setShowErrorModal(true);
		}
		setIsSubmitLoading(false);
		setOpenAlertMessage(true);
	};

	useEffect(() => {
		if (show) {
			(async () => {
				setIsLoading(true);
				const componentTypes = await getComponentTypeByPart(partId);
				const { data: partData } = await getPartById(partId);
				setPart(partData?.data.result);

				if (componentTypes.data?.data?.success) {
					setPartComponentTypes(componentTypes.data?.data?.result);
				} else {
					onHide(true);
					setMessage("Error, part not found. Try again");
					setOpenAlertMessage(true);
					setPartComponentTypeForEdit(null);
				}

				setIsLoading(false);
			})();
		}
	}, [show]);

	useEffect(() => {
		if (show && partComponentTypeForEdit) {
			const { partId, componentTypeId } = partComponentTypeForEdit!;
			setValue("partId", partId);
			setValue("componentTypeId", componentTypeId);
		}
	}, [partComponentTypeForEdit]);

	return (
		<>
			<>
				<Modal
					open={show}
					handleClose={() => {
						onHide(true);
					}}
					id="partComponentTypes"
					title={
						part?.partId === partId
							? `Component Types of ${part?.partName}`
							: ""
					}
					maxWidth="md"
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Modal.Content>
							{isLoading && (
								<Grid
									container
									justifyContent="center"
									alignItems="center"
									style={{ minHeight: "500px" }}
								>
									<CircularProgress size="5rem" />
								</Grid>
							)}

							{!isLoading && (
								<>
									<Grid container spacing={2}>
										<Grid item md={12} xs={12}>
											<DataTable
												partComponentTypes={partComponentTypes!}
												onSelectionChanged={setComponentTypesSelected}
											/>
										</Grid>
									</Grid>
								</>
							)}
						</Modal.Content>
						<Modal.Actions style={{ padding: "2rem" }}>
							<Grid container spacing={2} justifyContent="flex-end">
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={() => {
											onHide(false);
											// setPartForEdit(null)
										}}
									>
										Cancel
									</Button>
								</Grid>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										type="submit"
									>
										Save
									</Button>
								</Grid>
							</Grid>
						</Modal.Actions>
					</form>
				</Modal>
				<Snackbar
					open={openAlertMessage}
					autoHideDuration={2500}
					onClose={handleClose}
				>
					{messageType == "success" ? (
						<Alert severity="success" onClose={handleClose}>
							{message}
						</Alert>
					) : (
						<Alert severity="error" onClose={handleClose}>
							{message}
						</Alert>
					)}
				</Snackbar>
			</>
			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
